/* @flow */
import _ from 'lodash';

import API from 'lib/RemindAPI';

import Chat from 'entities/Chat';
import ChatMessageActions from 'chat/actions/ChatMessageActions';
import ChatMessage from 'entities/ChatMessage';
import ChatMessages from 'collections/ChatMessages';
import Collection from 'lib/Collection';
import Group from 'entities/Group';
import { CHATS_PAGE_SIZE } from 'chat/constants/ChatConstants';
import { rootGroup } from 'entities/RootGroup';

class Chats extends Collection {
  constructor(models: ?Object | Array<Object>, options: Object = {}) {
    super(models, options);
    this.sort();
  }

  comparator(a: Chat, b: Chat) {
    if (a.isNew()) { return -1; }
    if (b.isNew()) { return 1; }
    return b.getSortKey() > a.getSortKey() ? 1 : -1;
  }

  initialize(): void {
    this.url = '/chats';
    this.model = Chat;
  }

  setPosition(chat: Chat, position: number): void {
    this.remove(chat);
    this.add(chat, { at: position });
  }

  getNewestUpdatedAt(): ?Date {
    const newestChat = _.maxBy(this.models, chat => chat.getUpdatedAt());
    return newestChat ? newestChat.getUpdatedAt() : null;
  }

  getOldestUpdatedAt(): ?Date {
    const oldestChat = _.minBy(this.models, chat => chat.getUpdatedAt());
    return oldestChat ? oldestChat.getUpdatedAt() : null;
  }

  getLastMessage(): ?ChatMessage {
    return this.isEmpty() ? null : this.first().getLastMessage();
  }

  parse(response: Object): Object {
    // HACK to get v2/chats messages in this response
    if (response.messages) {
      const chatsMessages: {[key: string]: ChatMessages} = {};
      for (const chatId in response.messages) {
        // Protect ourselves from a backend bug that includes messages from other threads
        // in the pre-loaded message collection
        // TODO: Remove .filter when https://app.asana.com/0/32507888495125/158289850714897 is fixed
        const messagesData = response.messages[chatId].filter(m => m.chat.uuid === chatId);
        const messagesCollection = ChatMessages.fromJSON(messagesData);
        chatsMessages[chatId] = messagesCollection;
      }
      ChatMessageActions.updateChatsMessages(chatsMessages);
    }

    return response.chats;
  }

  selectChatsInGroup(group: Group): Chats {
    if (group.isRoot()) {
      return this;
    }
    // new chats aren't associated with any group, they just show up in whatever group
    // you were viewing at time of creation and are deleted if you switch groups
    return new Chats(this.filter(chat => chat.isInGroup(group.id) || chat.isNew()));
  }

  static setStateForAllChats(state: string): Promise<void> {
    return API.request('/chats', 'PATCH', {
      chats: { state },
    });
  }

  static enableRepliesForAllChats(): Promise<void> {
    return this.setStateForAllChats('open');
  }

  static disableRepliesForAllChats(): Promise<void> {
    return this.setStateForAllChats('replies_disabled');
  }

  static loadForGroup(groupId: number): Promise<Chats> {
    return Chats.request(null, groupId, false);
  }

  static search(query: string): Promise<Chats> {
    // Include memberships so matching matching against memberships works in chat list
    return Chats.request(query);
  }

  static request(query: ?string, groupId: number = rootGroup.id, includeMemberships: boolean = true): Promise<Chats> {
    const params = {
      limit: CHATS_PAGE_SIZE,
      // null query sent as empty
      q: query,
      group_id: groupId,
      // We load the root group messages at start so no need to get messages for each group.
      // When we do include messages, only get the unreads, assuming that's all people really care about.
      include_messages: (groupId === rootGroup.id) ? 'unreads' : false,
      include_memberships: includeMemberships,
    };
    // web's root group means no group to the backend
    if (groupId === rootGroup.id) {
      delete params['group_id'];
    }
    return API.request('/chats', 'GET', params)
      .then(response => Chats.fromJSON(response));
  }
}

export default Chats;



// WEBPACK FOOTER //
// ./src/collections/Chats.js